/**
 * v0.5① 日報機能 P-3 (§14-3-A 明細行): line_items (denomination=金種明細 / general=領収書明細)。
 *
 * ⚠️ スコープ限定 (次セッションが「不足」と誤認しないこと): 金種明細の「前日 (読取専用)」欄は
 * バックエンドの line_items carryover 未実装 (lib/daily-report/carryover.ts 冒頭コメント §3-6-a③
 * で明記済み、見送り) のため実データを出せない。`GET /daily-reports/form` は副作用 (未作成なら
 * 新規 draft を作る) を持つため、プレビュー目的で前日分だけ呼ぶのは安全ではない。本コンポーネントは
 * 「前日」欄をプレースホルダ表示に留め、実データ配線は専用のバックエンド endpoint 追加とセットで
 * 次回以降に行う。
 */
import { useState } from 'react';
import { Minus, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/Button';
import type {
  DailyReportField, DailyReportLineItemRow, DailyReportLineItemPutRow, Denomination,
  DailyReportLineItemFieldOptions,
} from '@/lib/daily-report-api';

const moneyFmt = (n: number) => `¥${n.toLocaleString()}`;

export function DenominationLineItemsSection({
  field, rows, denominations, disabled, onRowsChange,
}: {
  field: DailyReportField;
  rows: DailyReportLineItemRow[];
  denominations: Denomination[];
  disabled: boolean;
  onRowsChange: (rows: DailyReportLineItemPutRow[]) => void;
}) {
  // (denomination_id, unit_id) → quantity のローカル state (0始まり、既存行があれば初期値に反映)
  const quantityByKey = new Map(rows.filter((r) => r.denomination_id && r.unit_id)
    .map((r) => [`${r.denomination_id}:${r.unit_id}`, r.quantity ?? 0]));
  const [localQty, setLocalQty] = useState<Map<string, number>>(quantityByKey);

  // テナントが定義した (金種 × まとめ単位) の全組み合わせを縦積みで表示する (§14-3-A #12)。
  const combos = denominations
    .filter((d) => d.is_active)
    .flatMap((d) => d.units.map((u) => ({ denomination: d, unit: u })));

  const emit = (next: Map<string, number>) => {
    const nextRows: DailyReportLineItemPutRow[] = combos.map(({ denomination, unit }) => ({
      denomination_id: denomination.denomination_id,
      unit_id: unit.unit_id,
      quantity: next.get(`${denomination.denomination_id}:${unit.unit_id}`) ?? 0,
    }));
    onRowsChange(nextRows);
  };

  const setQty = (key: string, qty: number) => {
    const clamped = Math.max(0, Math.min(999999, Math.floor(qty) || 0));
    const next = new Map(localQty);
    next.set(key, clamped);
    setLocalQty(next);
    emit(next);
  };

  if (combos.length === 0) {
    return <p className="text-body text-text-sub">金種マスタが未登録です。設定から金種を登録してください。</p>;
  }

  let total = 0;
  return (
    <div className="space-y-2">
      {combos.map(({ denomination, unit }) => {
        const key = `${denomination.denomination_id}:${unit.unit_id}`;
        const qty = localQty.get(key) ?? 0;
        const amount = denomination.face_value * unit.pieces_per_unit * qty;
        total += amount;
        return (
          <div key={key} className="border border-surface-border rounded-lg bg-white p-3">
            <div className="text-body text-text-body font-semibold">
              {denomination.face_value.toLocaleString()}{denomination.currency_code === 'JPY' ? '円' : ` ${denomination.currency_code}`}
              {unit.unit_code !== 'piece' && ` ${unit.unit_code} (1${unit.unit_code}=${unit.pieces_per_unit.toLocaleString()}枚)`}
            </div>
            <div className="mt-1.5 flex items-center justify-between text-caption text-text-muted">
              <span>前日 — (表示は今後対応)</span>
            </div>
            <div className="mt-1 flex items-center gap-2">
              <span className="text-caption text-text-sub w-8">当日</span>
              <button type="button" aria-label="1減らす" disabled={disabled}
                className="w-11 h-11 shrink-0 inline-flex items-center justify-center rounded border border-surface-border text-text-body hover:bg-surface-sunken disabled:opacity-45"
                onClick={() => setQty(key, qty - 1)}>
                <Minus size={16} />
              </button>
              <input
                type="text" inputMode="numeric" pattern="[0-9]*" disabled={disabled}
                className="w-20 min-h-[44px] text-h3 text-center rounded border border-surface-border focus-ring appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none disabled:bg-surface-sunken disabled:text-text-sub"
                value={qty}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^0-9]/g, '');
                  setQty(key, v === '' ? 0 : Number(v));
                }}
              />
              <button type="button" aria-label="1増やす" disabled={disabled}
                className="w-11 h-11 shrink-0 inline-flex items-center justify-center rounded border border-surface-border text-text-body hover:bg-surface-sunken disabled:opacity-45"
                onClick={() => setQty(key, qty + 1)}>
                <Plus size={16} />
              </button>
              <span className="text-caption text-text-sub">本/枚</span>
            </div>
            <div className="mt-1.5 text-caption text-text-sub">
              金額 <span className="text-body text-text-body font-semibold">{moneyFmt(amount)}</span> (自動計算)
            </div>
          </div>
        );
      })}
      <div className="sticky bottom-16 sm:bottom-2 border border-surface-border rounded-lg bg-surface-sunken px-3 py-2 flex items-center justify-between">
        <span className="text-body text-text-sub">合計</span>
        <span className="text-h3 text-text-main">{moneyFmt(total)}</span>
      </div>
      <p className="sr-only" aria-live="polite">{field.label} 合計 {moneyFmt(total)}</p>
    </div>
  );
}

export function GeneralLineItemsSection({
  fieldOptions, rows, disabled, onRowsChange,
}: {
  fieldOptions: Extract<DailyReportLineItemFieldOptions, { line_item_kind: 'general' }>;
  rows: DailyReportLineItemRow[];
  disabled: boolean;
  onRowsChange: (rows: DailyReportLineItemPutRow[]) => void;
}) {
  const [localRows, setLocalRows] = useState<{ localId: string; values: Record<string, string | number> }[]>(
    () => rows.map((r) => ({ localId: r.line_item_id, values: r.values ?? {} })),
  );
  const [expanded, setExpanded] = useState(false);
  const [undoState, setUndoState] = useState<{ localId: string; values: Record<string, string | number>; index: number } | null>(null);

  const emit = (next: { localId: string; values: Record<string, string | number> }[]) => {
    onRowsChange(next.map((r) => ({ values: r.values })));
  };

  const addRow = () => {
    const next = [...localRows, { localId: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, values: {} }];
    setLocalRows(next);
    setExpanded(true);
  };

  const updateRow = (localId: string, key: string, value: string | number) => {
    const next = localRows.map((r) => (r.localId === localId ? { ...r, values: { ...r.values, [key]: value } } : r));
    setLocalRows(next);
    emit(next);
  };

  // §14-3-A「明細行の削除 Undo」: 即削除せずリストから隠し、5秒間 [元に戻す] を出す。
  const removeRow = (localId: string) => {
    const index = localRows.findIndex((r) => r.localId === localId);
    if (index === -1) return;
    const target = localRows[index]!;
    const next = localRows.filter((r) => r.localId !== localId);
    setLocalRows(next);
    emit(next);
    setUndoState({ localId: target.localId, values: target.values, index });
    setTimeout(() => setUndoState((cur) => (cur?.localId === localId ? null : cur)), 5000);
  };

  const undoRemove = () => {
    if (!undoState) return;
    const next = [...localRows];
    next.splice(undoState.index, 0, { localId: undoState.localId, values: undoState.values });
    setLocalRows(next);
    emit(next);
    setUndoState(null);
  };

  const visibleRows = expanded ? localRows : localRows.slice(0, 20);
  const amountKey = fieldOptions.amount_column_key;
  const subtotal = amountKey
    ? localRows.reduce((sum, r) => sum + (typeof r.values[amountKey] === 'number' ? (r.values[amountKey] as number) : 0), 0)
    : null;

  return (
    <div className="space-y-2">
      {localRows.length === 0 && <p className="text-body text-text-sub">明細行はまだありません。</p>}
      {visibleRows.map((row) => (
        <div key={row.localId} className="border border-surface-border rounded-lg bg-white p-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {fieldOptions.columns.map((col) => (
              <div key={col.key}>
                <label className="text-caption text-text-sub">{col.label}{col.required && <span className="ml-0.5">*</span>}</label>
                <input
                  type={col.type === 'number' ? 'number' : col.type === 'date' ? 'date' : 'text'}
                  inputMode={col.type === 'number' ? 'decimal' : undefined}
                  disabled={disabled}
                  className={`mt-0.5 w-full min-h-[44px] px-2 py-1.5 rounded border border-surface-border focus-ring disabled:bg-surface-sunken disabled:text-text-sub ${col.type === 'number' ? 'text-h3 appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none' : 'text-body'}`}
                  value={row.values[col.key] ?? ''}
                  onChange={(e) => updateRow(row.localId, col.key, col.type === 'number' ? Number(e.target.value) : e.target.value)}
                />
              </div>
            ))}
          </div>
          <div className="mt-2 flex justify-end">
            <Button variant="ghost" size="sm" leftIcon={<Trash2 size={14} />} disabled={disabled} onClick={() => removeRow(row.localId)}>削除</Button>
          </div>
        </div>
      ))}
      {!expanded && localRows.length > 20 && (
        <Button variant="ghost" size="sm" onClick={() => setExpanded(true)}>すべて表示 (残り{localRows.length - 20}件)</Button>
      )}
      <Button variant="secondary" size="sm" disabled={disabled} onClick={addRow}>+ 明細行を追加</Button>
      {subtotal !== null && (
        <div className="text-body text-text-sub">小計 <span className="text-h3 text-text-main">¥{subtotal.toLocaleString()}</span></div>
      )}
      {undoState && (
        <div role="status" className="fixed bottom-20 left-1/2 -translate-x-1/2 bg-chrome-base text-white rounded-md shadow-overlay px-4 py-2.5 flex items-center gap-3 z-50">
          <span className="text-body">1件削除しました</span>
          <button type="button" className="text-body font-bold underline underline-offset-2 min-h-[44px] px-1 inline-flex items-center" onClick={undoRemove}>元に戻す</button>
        </div>
      )}
    </div>
  );
}

